/**
 * Created: 2026-10-29
 * Initial commit: Port event start / score entry / completion from mini to web admin
 *
 * Dialog for an event organizer to finalize the player roster, pick the game format
 * (and fixed-partner teams for Doubles), and start the event.
 */

import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import {
  EventPlanner,
  resolveStartFormat,
  sortBySeeding,
  sortTeamsBySeeding,
  toCompetitors,
} from '@tc-app/shared/event'
import { getFormatLabel } from '@tc-app/shared/labels'
import { useState } from 'react'
import { GameFormat, type Gender, StartEventDocument } from '../gql/graphql'
import UserAvatar from './UserAvatar'
import UserSelector, { type UserSelectorUser } from './UserSelector'

gql`
  mutation StartEvent(
    $eventId: UUID
    $competitors: [CompetitorInput!]!
    $gameFormat: GameFormat!
  ) {
    startEvent(
      eventId: $eventId
      competitors: $competitors
      gameFormat: $gameFormat
    ) {
      ... on Event {
        id
        status
        gameFormat
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

export type StartPlayer = {
  // Unique key within the roster: the signup id, or the user id for added players
  id: string
  user: {
    id?: string | null
    name?: string | null
    avatar?: string | null
    mobile?: string | null
    gender?: Gender | null
  }
  rating: number
  signupTime: number
}

const START_FORMATS = [
  GameFormat.Singles,
  GameFormat.RotatingDoubles,
  GameFormat.Doubles,
]

interface EventStartDialogProps {
  event: {
    id: string
    siteId?: string | null
    gameFormat: GameFormat
    drawSize?: number | null
    scheduledStartTime: string
    duration: number
  }
  initialPlayers: StartPlayer[]
  onClose: () => void
  onStarted: () => void
}

function getStartWarning(scheduledStartTime: string, duration: number): string | null {
  const now = Date.now()
  const startMs = new Date(scheduledStartTime).getTime()
  const endMs = startMs + duration * 3600_000
  if (now > endMs) return '活动已超过结束时间，确认要开始比赛吗？'
  if (startMs - now > 5 * 60_000) return '距活动开始时间超过5分钟，确认要开始比赛吗？'
  return null
}

/**
 * Lets the organizer pick 2 players at a time to form fixed-partner Doubles teams;
 * the last 2 remaining players form the final team automatically.
 */
function TeamPicker({
  players,
  teams,
  onChange,
}: {
  players: StartPlayer[]
  teams: StartPlayer[][]
  onChange: (teams: StartPlayer[][]) => void
}) {
  const [selection, setSelection] = useState<string[]>([])
  const assigned = new Set(teams.flat().map((p) => p.id))
  const remaining = players.filter((p) => !assigned.has(p.id))

  const toggle = (id: string) => {
    if (selection.includes(id)) {
      setSelection(selection.filter((s) => s !== id))
    } else if (selection.length < 2) {
      setSelection([...selection, id])
    }
  }

  const lockTeam = () => {
    const team = remaining.filter((p) => selection.includes(p.id))
    const rest = remaining.filter((p) => !selection.includes(p.id))
    // The last 2 players can only form the final team
    onChange(rest.length === 2 ? [...teams, team, rest] : [...teams, team])
    setSelection([])
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        选择 2 名选手组成一队（已分组 {teams.length}/{Math.floor(players.length / 2)}{' '}
        队）
      </Typography>
      <List dense disablePadding>
        {teams.map((team, idx) => (
          <ListItem
            key={team.map((p) => p.id).join('-')}
            disableGutters
            secondaryAction={
              <IconButton
                size="small"
                onClick={() => onChange(teams.filter((_, i) => i !== idx))}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2" sx={{ minWidth: 40 }}>
              {idx + 1} 队
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {team.map((p) => (
                <UserAvatar key={p.id} user={p.user} />
              ))}
            </Box>
          </ListItem>
        ))}
      </List>
      {remaining.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {remaining.map((p) => (
              <Chip
                key={p.id}
                label={p.user.name ?? '-'}
                color={selection.includes(p.id) ? 'primary' : 'default'}
                onClick={() => toggle(p.id)}
              />
            ))}
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={selection.length !== 2}
            onClick={lockTeam}
          >
            组队
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default function EventStartDialog({
  event,
  initialPlayers,
  onClose,
  onStarted,
}: EventStartDialogProps) {
  const [startEvent, { loading }] = useMutation(StartEventDocument)
  const [players, setPlayers] = useState<StartPlayer[]>(() =>
    sortBySeeding(initialPlayers),
  )
  const [selectedFormat, setSelectedFormat] = useState<GameFormat | null>(null)
  const [teams, setTeams] = useState<StartPlayer[][]>([])
  const [userToAdd, setUserToAdd] = useState<UserSelectorUser | null>(null)

  const supportedFormats = START_FORMATS.filter((f) =>
    EventPlanner.supportsPlayerCount(players.length, f),
  )
  // Resolved format wins when unambiguous; otherwise the organizer must choose
  const resolvedFormat = resolveStartFormat(
    players.length,
    event.gameFormat,
  ) as GameFormat | null
  const format =
    selectedFormat && supportedFormats.includes(selectedFormat)
      ? selectedFormat
      : resolvedFormat
  const canAddPlayer = !event.drawSize || players.length < event.drawSize
  const teamsComplete = teams.length === players.length / 2
  const canStart =
    format !== null && (format !== GameFormat.Doubles || teamsComplete) && !loading

  const updatePlayers = (next: StartPlayer[]) => {
    setPlayers(next)
    // Roster changes invalidate any team assignment
    setTeams([])
  }

  const addPlayer = () => {
    if (!userToAdd || players.some((p) => p.user.id === userToAdd.id)) return
    updatePlayers(
      sortBySeeding([
        ...players,
        { id: userToAdd.id, user: userToAdd, rating: 0, signupTime: Date.now() },
      ]),
    )
    setUserToAdd(null)
  }

  const doStart = async () => {
    if (!format) return
    const roster =
      format === GameFormat.Doubles ? sortTeamsBySeeding(teams).flat() : players
    const result = await startEvent({
      variables: {
        eventId: event.id,
        competitors: toCompetitors(roster, format),
        gameFormat: format,
      },
    })
    const response = result.data?.startEvent
    if (response?.__typename === 'GraphqlRequestError') {
      message.error(response.message ?? '开始比赛失败')
      return
    }
    message.success('比赛已开始')
    onStarted()
  }

  const handleStart = () => {
    if (!format) return
    const warning = getStartWarning(event.scheduledStartTime, event.duration)
    message.prompt(
      `${warning ? `${warning}\n` : ''}确认以${getFormatLabel(format)}形式开始比赛？`,
      {
        title: '确认开始比赛',
        noLabel: '返回',
        yesLabel: '开始比赛',
        onYesButton: doStart,
      },
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>开始比赛</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          参赛选手 ({players.length}
          {event.drawSize ? ` / ${event.drawSize}` : ''})
        </Typography>
        {players.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            暂无选手报名
          </Typography>
        ) : (
          <List dense disablePadding>
            {players.map((p, idx) => (
              <ListItem
                key={p.id}
                disableGutters
                secondaryAction={
                  <IconButton
                    size="small"
                    onClick={() => updatePlayers(players.filter((x) => x.id !== p.id))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', minWidth: 24, mr: 1 }}
                >
                  {idx + 1}.
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <UserAvatar user={p.user} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    等级: {p.rating > 0 ? p.rating.toFixed(2) : '—'}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        {canAddPlayer && event.siteId && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
            <UserSelector
              siteId={event.siteId}
              value={userToAdd}
              onChange={setUserToAdd}
            />
            <Button variant="outlined" disabled={!userToAdd} onClick={addPlayer}>
              添加选手
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          {supportedFormats.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              当前人数（{players.length}人）不支持任何比赛形式
            </Typography>
          ) : (
            <TextField
              select
              size="small"
              label="比赛形式"
              value={format ?? ''}
              onChange={(e) => {
                setSelectedFormat(e.target.value as GameFormat)
                setTeams([])
              }}
              sx={{ minWidth: 200 }}
            >
              {supportedFormats.map((f) => (
                <MenuItem key={f} value={f}>
                  {getFormatLabel(f)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        {format === GameFormat.Doubles && (
          <Box sx={{ mt: 2 }}>
            <TeamPicker players={players} teams={teams} onChange={setTeams} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            updatePlayers(sortBySeeding(initialPlayers))
            setSelectedFormat(null)
          }}
          sx={{ mr: 'auto' }}
        >
          重置
        </Button>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" disabled={!canStart} onClick={handleStart}>
          开始比赛
        </Button>
      </DialogActions>
    </Dialog>
  )
}
